import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import {
  generateStudyLessonCall,
  requestLessonPodcastCall,
  saveLessonProgressCall,
} from "../../redux/api/studyPlan.api";
import { cleanLessonText, masteryEmoji } from "./data";
import StudyShell from "./StudyShell";

const FEEDBACK = [
  { id: "easy", label: "Too easy", emoji: "😊" },
  { id: "right", label: "About right", emoji: "👍" },
  { id: "hard", label: "Too hard", emoji: "😓" },
  { id: "irrelevant", label: "Not relevant", emoji: "🚫" },
];

function sameIndexes(question, picked) {
  const wanted = [...(question.correctIndexes || [])].map(Number).sort((a, b) => a - b);
  const got = [...picked].map(Number).sort((a, b) => a - b);
  return wanted.length > 0 && wanted.length === got.length && wanted.every((value, index) => value === got[index]);
}

export default function StudyLessonPage() {
  const { planId, topicId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(true);
  const [selected, setSelected] = useState([]);
  const [revealed, setRevealed] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [podcastNote, setPodcastNote] = useState("");

  useEffect(() => {
    let alive = true;
    setBusy(true);
    generateStudyLessonCall(planId, topicId).then((response) => {
      if (!alive) return;
      if (response?.lesson?.steps?.length) {
        setLesson(response.lesson);
        setProgress(response.lesson.progress);
        setError("");
      } else {
        setError(response?.message || "The lesson could not be written. Try again.");
      }
      setBusy(false);
    });
    return () => {
      alive = false;
    };
  }, [planId, topicId]);

  useEffect(() => {
    setSelected([]);
    setRevealed(false);
    setCardOpen(false);
  }, [progress?.phase, progress?.stepIndex, progress?.quizIndex, progress?.cardIndex]);

  const step = lesson?.steps?.[progress?.stepIndex || 0];
  const question = useMemo(() => {
    if (!lesson || !progress) return null;
    if (progress.phase === "quiz") return lesson.finalQuiz?.[progress.quizIndex || 0] || null;
    if (step?.kind === "check") return step.question;
    return null;
  }, [lesson, progress, step]);
  const card = lesson?.flashcards?.[progress?.cardIndex || 0];

  const save = async (body) => {
    if (!lesson) return;
    setBusy(true);
    setError("");
    const response = await saveLessonProgressCall(planId, lesson.id, body);
    setBusy(false);
    if (!response?.progress) {
      setError(response?.message || "Progress could not be saved.");
      return;
    }
    setProgress(response.progress);
  };

  const toggleChoice = (index) => {
    if (revealed || !question) return;
    if (question.type === "multiple") {
      setSelected((current) => (current.includes(index) ? current.filter((item) => item !== index) : [...current, index]));
      return;
    }
    setSelected([index]);
  };

  const continueReading = () => {
    if (!lesson || !progress || step?.kind !== "read") return;
    save({
      completedStepIds: [...(progress.completedStepIds || []), step.id],
      answers: progress.answers || [],
      cardIndex: 0,
      feedback: null,
    });
  };

  const continueAnswer = () => {
    if (!lesson || !progress || !question || !selected.length) return;
    const scope = progress.phase === "quiz" ? "final" : "check";
    const completedStepIds = scope === "check" && step
      ? [...(progress.completedStepIds || []), step.id]
      : progress.completedStepIds || [];
    save({
      completedStepIds,
      answers: [
        ...(progress.answers || []),
        { questionId: question.id, scope, selectedIndexes: selected },
      ],
      cardIndex: progress.cardIndex || 0,
      feedback: progress.feedback,
    });
  };

  const recordPodcast = async () => {
    if (!lesson || busy) return;
    if (lesson.podcast?.url) return;
    setBusy(true);
    setPodcastNote("Recording the podcast…");
    const response = await requestLessonPodcastCall(planId, lesson.id);
    setBusy(false);
    if (!response?.url) {
      setPodcastNote(response?.message || "The podcast is not ready yet.");
      return;
    }
    setPodcastNote("");
    setLesson((current) => (current ? {
      ...current,
      podcast: { url: response.url, title: response.title || current.title, turns: response.turns || [] },
    } : current));
  };

  const correct = question && revealed ? sameIndexes(question, selected) : false;

  return (
    <StudyShell>
      <div className="study-lesson">
        <button type="button" className="study-link" onClick={() => navigate(`/study/plan/${planId}`)}>Back to plan</button>
        {busy && !lesson ? <CircularProgress size={28} sx={{ mt: 3 }} /> : null}
        {error ? <div className="study-error">{error}</div> : null}
        {lesson && progress ? (
          <article className="study-lesson-card" style={{ marginTop: 16 }}>
            <div className="study-kicker" style={{ background: "rgba(37,99,235,0.12)", color: "#1d4ed8" }}>
              {progress.phase === "done" ? "Finished" : progress.phase === "cards" ? "Flashcards" : progress.phase === "quiz" ? "Quiz" : progress.phase === "feedback" ? "How was it?" : "Lesson"}
            </div>
            <div className="study-bar" style={{ margin: "14px 0 8px" }}>
              <span style={{ width: `${Math.max(0, Math.min(100, progress.completionPercent || 0))}%` }} />
            </div>

            {progress.phase === "steps" && step?.kind === "read" ? (
              <>
                <h1>{step.heading || lesson.title}</h1>
                <div className="study-body">{cleanLessonText(step.body)}</div>
                <div className="study-hero-actions">
                  <button type="button" className="study-primary" disabled={busy} onClick={continueReading} style={{ background: "#1e1b4b", color: "#fff" }}>
                    Continue
                  </button>
                </div>
              </>
            ) : null}

            {question ? (
              <>
                <h1>{cleanLessonText(question.prompt)}</h1>
                {(question.choices || []).map((choice, index) => {
                  const picked = selected.includes(index);
                  const showGood = revealed && (question.correctIndexes || []).map(Number).includes(index);
                  const showBad = revealed && picked && !showGood;
                  const className = ["study-answer", picked ? "picked" : "", showGood ? "good" : "", showBad ? "bad" : ""].filter(Boolean).join(" ");
                  return (
                    <button key={`${choice}-${index}`} type="button" className={className} onClick={() => toggleChoice(index)}>
                      {cleanLessonText(choice)}
                    </button>
                  );
                })}
                {revealed ? (
                  <p className="study-body">
                    {correct ? "Correct." : "Not quite."} {cleanLessonText(question.explanation)}
                  </p>
                ) : null}
                <div className="study-hero-actions">
                  {!revealed ? (
                    <button type="button" className="study-primary" disabled={!selected.length} onClick={() => setRevealed(true)} style={{ background: "#1e1b4b", color: "#fff" }}>
                      Check
                    </button>
                  ) : (
                    <button type="button" className="study-primary" disabled={busy} onClick={continueAnswer} style={{ background: "#1e1b4b", color: "#fff" }}>
                      Continue
                    </button>
                  )}
                </div>
              </>
            ) : null}

            {progress.phase === "cards" && card ? (
              <>
                <h1>Card {(progress.cardIndex || 0) + 1} of {lesson.flashcards.length}</h1>
                <div className="study-flip" onClick={() => setCardOpen(true)} role="button" tabIndex={0}>
                  {cardOpen ? cleanLessonText(card.back) : cleanLessonText(card.front)}
                </div>
                <div className="study-hero-actions">
                  <button
                    type="button"
                    className="study-primary"
                    disabled={busy || !cardOpen}
                    onClick={() => save({
                      completedStepIds: progress.completedStepIds || [],
                      answers: progress.answers || [],
                      cardIndex: (progress.cardIndex || 0) + 1,
                      feedback: null,
                    })}
                    style={{ background: "#1e1b4b", color: "#fff" }}
                  >
                    Next card
                  </button>
                </div>
              </>
            ) : null}

            {progress.phase === "feedback" ? (
              <>
                <h1>How did this lesson feel?</h1>
                {FEEDBACK.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    className="study-choice"
                    disabled={busy}
                    onClick={() => save({
                      completedStepIds: progress.completedStepIds || [],
                      answers: progress.answers || [],
                      cardIndex: progress.cardIndex || 0,
                      feedback: item.id,
                    })}
                  >
                    <strong>{item.emoji} {item.label}</strong>
                  </button>
                ))}
              </>
            ) : null}

            {progress.phase === "done" ? (
              <>
                <div className="study-mastery">{masteryEmoji(progress.masteryPercent)}</div>
                <h1>{progress.masteryPercent != null ? `${progress.masteryPercent}% mastery` : "Lesson complete"}</h1>
                <p className="study-body">{progress.recommendationExplanation || progress.masteryExplanation}</p>
                {(lesson.preferences || []).includes("podcast") || lesson.podcast ? (
                  <div style={{ marginTop: 16 }}>
                    {lesson.podcast?.url ? (
                      <audio controls src={lesson.podcast.url} style={{ width: "100%" }} />
                    ) : (
                      <button type="button" className="study-choice" disabled={busy} onClick={recordPodcast}>
                        <strong>Make a podcast of this lesson</strong>
                      </button>
                    )}
                    {podcastNote ? <p className="study-meta">{podcastNote}</p> : null}
                  </div>
                ) : null}
                <div className="study-hero-actions">
                  <button type="button" className="study-choice" style={{ width: "auto" }} disabled={busy} onClick={() => save({ action: "review" })}>
                    Review
                  </button>
                  <button type="button" className="study-primary" disabled={busy} onClick={() => save({ action: "practice" })} style={{ background: "#1e1b4b", color: "#fff" }}>
                    Practice again
                  </button>
                  {lesson.nextTopic ? (
                    <button type="button" className="study-primary" onClick={() => navigate(`/study/plan/${planId}/topic/${lesson.nextTopic.id}`)} style={{ background: "#1e1b4b", color: "#fff" }}>
                      Next: {lesson.nextTopic.title}
                    </button>
                  ) : null}
                </div>
              </>
            ) : null}
          </article>
        ) : null}
      </div>
    </StudyShell>
  );
}
